import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { withDevtools } from '@ngrx-toolkit/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, exhaustMap, map, mergeMap, of, pipe, switchMap, tap, throwError } from 'rxjs';

import { CancellationService, CancellationUrlType } from '@ph-core/services/cancellation.service';
import { ConfigService } from '@ph-core/services/config/config.service';
import { ContractSaveFacade } from '@ph-core/services/contract-save.facade';
import { PhDialog } from '@ph-core/services/dialog';
import { NotificationService } from '@ph-core/services/notification/notification.service';
import { ContractType, RemittedContractResponse } from '@ph-model/api';
import { PendingContractResponse } from '@ph-model/api/response/pending-contract.response.model';
import { ContractFilter } from '@ph-model/filters/contract-filter.model';
import { Filter } from '@ph-model/filters/filter';
import { SearchFilter } from '@ph-model/filters/search-filter';
import { UserResponse } from '@ph-model/login';
import { CancellationAlertsResponse } from '@ph-model/response/cancellation-alerts.response';
import { RemitService } from '@ph-remit/services/remit.service';
import { PhNotificationType } from '@ph-shared/components/notification/notification/notification.interface';
import { RemitSubmittedPopupComponent } from '@ph-shared/components/remit-submitted-popup/remit-submitted-popup.component';
import {
  PhStatusChipCategory,
  PhStatusChipType,
  StatusChipData,
} from '@ph-shared/components/status-chip/status-chip.interface';
import { UserStore, UserStoreType } from '@ph-store/user/user.store';

const storeKey = 'RemitStore';

export interface RemitState {
  loadingPendingContracts: boolean;
  pendingContractsLoaded: boolean;
  pendingContracts: ContractType[];
  pendingContractFilters: SearchFilter;
  pendingGridContracts: ContractType[];
  pendingToDeclineContracts: ContractType[];
  pendingToRemitContracts: ContractType[];
  remittedContracts: ContractType[];
  remittedFilters: SearchFilter;
  remittedGridContracts: ContractType[];
  remittedSelectedContracts: ContractType[];
  submittedPendingContracts: ContractType[];
  cancellationAlerts?: CancellationAlertsResponse;
}

const InitialState: RemitState = {
  loadingPendingContracts: false,
  pendingContractsLoaded: false,
  pendingContracts: [],
  pendingContractFilters: new SearchFilter(),
  pendingGridContracts: [],
  pendingToDeclineContracts: [],
  pendingToRemitContracts: [],
  remittedContracts: [],
  remittedFilters: new SearchFilter(),
  remittedGridContracts: [],
  remittedSelectedContracts: [],
  submittedPendingContracts: [],
  cancellationAlerts: undefined,
};

export type RemitStoreType = InstanceType<typeof RemitStore>;

export const RemitStore = signalStore(
  { providedIn: 'root' },
  withDevtools(storeKey),
  withState<RemitState>(InitialState),
  withMethods(
    (
      store,
      httpClient: HttpClient = inject(HttpClient),
      configService = inject(ConfigService),
      translate = inject(TranslateService),
      userStore: UserStoreType = inject(UserStore),
      dialog: PhDialog = inject(PhDialog),
      remitService: RemitService = inject(RemitService),
      notificationService: NotificationService = inject(NotificationService),
      cancellationService: CancellationService = inject(CancellationService),
      contractSaveFacade: ContractSaveFacade = inject(ContractSaveFacade)
    ) => {
      function _enrichContracts(resp: PendingContractResponse): PendingContractResponse {
        const updatedPendingContracts = resp.pendingContracts.map((contract: ContractType) => {
          const statuses: StatusChipData[] = [];

          if (contract.dupError) {
            statuses.push({
              tooltip: contract.dupMessage,
              type: PhStatusChipType.duplicate,
              category: PhStatusChipCategory.generic,
              label: translate.instant('statusChip.duplicate'),
            });
          }

          if (contract.remitWarning) {
            statuses.push({
              label: contract.remitTag,
              type: PhStatusChipType.info,
              category: PhStatusChipCategory.generic,
            });
          }

          if (contract.parentRequiredError) {
            statuses.push({
              tooltip: contract.parentRequiredMessage,
              type: PhStatusChipType.addon,
              category: PhStatusChipCategory.generic,
              label: translate.instant(configService.environment.verbiage.remitPage.statusChip.addon),
            });
          }

          let tagType: PhStatusChipType;
          if (contract.tagCode) {
            switch (contract.tagCode) {
              case 'A':
              case 'B':
                tagType = PhStatusChipType.inaudit;
                break;

              case 'C':
              case 'D':
                tagType = PhStatusChipType.cancel;
                break;
              default:
                tagType = PhStatusChipType.none;
                break;
            }
            if (tagType !== PhStatusChipType.none) {
              statuses.push({ label: contract.tagMessage, type: tagType, category: PhStatusChipCategory.generic });
            }
          }

          contract.statuses = statuses;

          if (contract.parentRequiredCode) {
            const parent = resp.pendingContracts.find(
              (c: ContractType) => contract.vin === c.vin && contract.parentRequiredCode === c.productCode
            );
            if (parent) {
              contract.parentEconId = parent.econContractId;
            }
          }

          return contract;
        });

        return { ...resp, pendingContracts: updatedPendingContracts };
      }

      function _getPendingContracts() {
        if (store.pendingContractsLoaded()) {
          return of({
            pendingContracts: store.pendingContracts(),
            pendingToRemitContracts: store.pendingToRemitContracts(),
            pendingToDeclineContracts: store.pendingToDeclineContracts(),
            erroredContracts: { declinedErrorContracts: [], remittedErrorContracts: [] },
          });
        }

        const environment = configService.environment;
        patchState(store, { loadingPendingContracts: true, pendingContractsLoaded: false });
        const user = userStore.user();
        const allDealers =
          user?.allDealers?.map((dealer) => ({
            ...dealer,
            ...(user?.allDealers?.length === 1 && { cmsDealerNumber: user?.cmsDealerNumber || '' }),
          })) || [];

        return httpClient
          .post<PendingContractResponse>(`${environment.apiUrl}/getpendingcontracts`, {
            cmsdealernumber: user?.cmsDealerNumber || '',
            pendingToRemitContracts: store.pendingToRemitContracts(),
            pendingToDeclineContracts: store.pendingToDeclineContracts(),
            programId: user?.programId || '',
            allDealers: allDealers,
            pendingDays: user?.pendingDays || [],
          })
          .pipe(
            tap((response: PendingContractResponse) => {
              if (environment.features.suppressDealerCost) {
                remitService.suppressDealerCost(response.pendingContracts);
              }
            }),
            map((response: PendingContractResponse) => _enrichContracts(response)),
            tap((response: PendingContractResponse) => {
              userStore.updatePendingContractsCount(response.pendingContracts.length);
              const enrichedContracts = environment.features.remit.disableNewerContractsIf60DaysOldExist
                ? _disableNewerContractsIf60DaysOldExist(response.pendingContracts)
                : response.pendingContracts;

              patchState(store, {
                pendingContracts: enrichedContracts,
                pendingGridContracts: enrichedContracts,
                pendingToRemitContracts: response.pendingToRemitContracts,
                pendingToDeclineContracts: response.pendingToDeclineContracts,
                pendingContractFilters: RemitService.fetchPendingContractFilters(response.pendingContracts),
                loadingPendingContracts: false,
                pendingContractsLoaded: true,
              });
            }),
            catchError((error) => throwError(() => error))
          );
      }

      function _getMessage(type: 'A' | 'B' | 'C' | 'D'): string {
        const user = userStore.user();
        let message = '';
        if (user?.pendingDays?.checkPendingDays) {
          const pendingDays = user.pendingDays.checkPendingDays;
          for (let x = 0; x < pendingDays.length; x++) {
            if (pendingDays[x].rangeTagCode === type) {
              message = pendingDays[x].loginMessage;
            }
          }
        }

        return message;
      }

      function _disableNewerContractsIf60DaysOldExist(contracts: ContractType[]): ContractType[] {
        const has60DayOldContracts = contracts.some((c) => c.tagCode === 'C' || c.tagCode === 'D');

        if (!has60DayOldContracts) {
          return contracts;
        }

        return contracts.map((c) => ({ ...c, disabled: c.disabled || (c.tagCode !== 'C' && c.tagCode !== 'D') }));
      }

      return {
        filterPendingGrid(filters: SearchFilter) {
          const newSearchTerm =
            filters.searchPhrase !== undefined ? filters.searchPhrase : store.pendingContractFilters.searchPhrase();
          const contracts = RemitService.filterPendingGrid(filters, newSearchTerm, store.pendingContracts());

          patchState(store, { pendingContractFilters: filters, pendingGridContracts: contracts });
        },
        clearPendingFilters() {
          const currentFilters = store.pendingContractFilters();
          const pendingContractFilters = {
            ...currentFilters,
            month: currentFilters.month.map((f: ContractFilter) => ({ ...f, selected: false })),
            productCode: currentFilters.productCode.map((f: Filter) => ({ ...f, selected: false })),
            productType: currentFilters.productType.map((f: Filter) => ({ ...f, selected: false })),
            status: currentFilters.status.map((f: Filter) => ({ ...f, selected: false, excluded: false })),
            searchPhrase: '',
          };

          patchState(store, {
            pendingContractFilters,
          });
        },
        clearRemittedFilters() {
          const currentFilters = store.remittedFilters();
          const remittedFilters = {
            ...currentFilters,
            month: currentFilters.month.map((g: ContractFilter) => ({
              ...g,
              selected: false,
              batch: g.batch.map((b: Filter) => ({ ...b, selected: false })),
            })),
            productCode: currentFilters.productCode.map((f: Filter) => ({ ...f, selected: false })),
            searchPhrase: '',
          };

          patchState(store, {
            remittedFilters,
          });
        },
        addToPendingRemit(contract: ContractType) {
          const updatedContracts = store.pendingContracts().map((item: ContractType) => {
            if (item.econContractId === contract.econContractId) {
              return { ...item, addToRemit: true };
            }

            return item;
          });

          const updatedGridContracts = store.pendingGridContracts().map((item: ContractType) => {
            if (item.econContractId === contract.econContractId) {
              return { ...item, addToRemit: true };
            }

            return item;
          });

          patchState(store, {
            pendingContracts: updatedContracts,
            pendingGridContracts: updatedGridContracts,
            pendingToRemitContracts: [...store.pendingToRemitContracts(), { ...contract, addToRemit: true }],
          });
        },
        deleteFromPendingRemit(econContractId: string) {
          const updatedContracts = store.pendingContracts().map((item: ContractType) => {
            if (item.econContractId === econContractId) {
              return { ...item, addToRemit: false };
            }

            return item;
          });
          const updatedGridContracts = store.pendingGridContracts().map((item: ContractType) => {
            if (item.econContractId === econContractId) {
              return { ...item, addToRemit: false };
            }

            return item;
          });

          patchState(store, {
            pendingContracts: updatedContracts,
            pendingGridContracts: updatedGridContracts,
            pendingToRemitContracts: store
              .pendingToRemitContracts()
              .filter((item: ContractType) => item.econContractId !== econContractId),
          });
        },
        declinePendingContract(contractType: ContractType) {
          patchState(store, {
            pendingGridContracts: RemitService.declineAndUpdateContracts(store.pendingGridContracts(), contractType),
            pendingToDeclineContracts: [...store.pendingToDeclineContracts(), contractType],
          });
        },
        reinstatePendingContract(contractType: ContractType) {
          const contracts = RemitService.reinstateAndUpdateContracts(store.pendingGridContracts(), contractType);

          patchState(store, {
            pendingGridContracts: contracts,
            pendingToDeclineContracts: store
              .pendingToDeclineContracts()
              .filter((item: ContractType) => item.econContractId !== contractType.econContractId),
            pendingToRemitContracts: contracts.filter((item) => item.addToRemit === true),
          });
        },
        addAllToPendingDecline() {
          const addedContracts = store.pendingGridContracts().map((item: ContractType) => {
            if (!item.decline && !item.addToRemit && !item.isReadOnly) {
              return { ...item, decline: true, addToRemit: false };
            }

            return item;
          });

          patchState(store, {
            pendingGridContracts: addedContracts,
            pendingToDeclineContracts: addedContracts.filter((item: ContractType) => item.decline),
          });
        },

        removeAllFromPendingRemitOnly() {
          patchState(store, {
            pendingGridContracts: store.pendingGridContracts().map((item: ContractType) => {
              if (item.addToRemit === true) {
                item.addToRemit = false;
              }

              return item;
            }),
            pendingContracts: store.pendingContracts().map((item: ContractType) => {
              if (item.addToRemit === true) {
                item.addToRemit = false;
              }

              return item;
            }),
            pendingToRemitContracts: [],
          });
        },

        removeAllFromDeclinedRemitOnly() {
          const contracts = store
            .pendingGridContracts()
            .reduce(
              (prevValue: ContractType[], elem: ContractType) =>
                RemitService.reinstateAndUpdateContracts(prevValue, elem),
              store.pendingGridContracts()
            );
          const pendingContracts = store
            .pendingContracts()
            .reduce(
              (prevValue: ContractType[], elem: ContractType) =>
                RemitService.reinstateAndUpdateContracts(prevValue, elem),
              store.pendingContracts()
            );

          patchState(store, {
            pendingGridContracts: contracts,
            pendingContracts,
            pendingToDeclineContracts: [],
          });
        },

        filterRemittedGrid(filters: SearchFilter) {
          const newSearchTerm =
            filters.searchPhrase !== undefined ? filters.searchPhrase : store.remittedFilters().searchPhrase;
          const contracts = RemitService.filterRemittedGrid(filters, newSearchTerm, store.remittedContracts());

          patchState(store, { remittedFilters: filters, remittedGridContracts: contracts });
        },

        selectRemittedContract(contract: ContractType) {
          const updatedContracts = store.remittedContracts().map((item: ContractType) => {
            if (item.econContractId === contract.econContractId) {
              return { ...item, selected: true };
            }

            return item;
          });
          const updatedGridContracts = store.remittedGridContracts().map((item: ContractType) => {
            if (item.econContractId === contract.econContractId) {
              return { ...item, selected: true };
            }

            return item;
          });

          patchState(store, {
            remittedContracts: updatedContracts,
            remittedGridContracts: updatedGridContracts,
            remittedSelectedContracts: updatedContracts.filter((item: ContractType) => item.selected),
          });
        },

        deselectRemittedContract(contract: ContractType) {
          const updatedContracts = store.remittedContracts().map((item: ContractType) => {
            if (item.econContractId === contract.econContractId) {
              return { ...item, selected: false };
            }

            return item;
          });
          const updatedGridContracts = store.remittedGridContracts().map((item: ContractType) => {
            if (item.econContractId === contract.econContractId) {
              return { ...item, selected: false };
            }

            return item;
          });

          patchState(store, {
            remittedContracts: updatedContracts,
            remittedGridContracts: updatedGridContracts,
            remittedSelectedContracts: updatedContracts.filter((item: ContractType) => item.selected),
          });
        },

        selectAllRemittedGridContracts() {
          const gridContractIds = new Set(
            store.remittedGridContracts().map((item: ContractType) => item.econContractId)
          );
          const updatedContracts = store.remittedContracts().map((item: ContractType) => {
            if (gridContractIds.has(item.econContractId)) {
              return { ...item, selected: true };
            }

            return item;
          });
          const updatedGridContracts = store.remittedGridContracts().map((item: ContractType) => ({
            ...item,
            selected: true,
          }));

          patchState(store, {
            remittedContracts: updatedContracts,
            remittedGridContracts: updatedGridContracts,
            remittedSelectedContracts: updatedContracts.filter((item: ContractType) => item.selected),
          });
        },

        deselectAllRemittedGridContracts() {
          const gridContractIds = new Set(
            store.remittedGridContracts().map((item: ContractType) => item.econContractId)
          );
          const updatedContracts = store.remittedContracts().map((item: ContractType) => {
            if (gridContractIds.has(item.econContractId)) {
              return { ...item, selected: false };
            }

            return item;
          });
          const updatedGridContracts = store.remittedGridContracts().map((item: ContractType) => ({
            ...item,
            selected: false,
          }));

          patchState(store, {
            remittedContracts: updatedContracts,
            remittedGridContracts: updatedGridContracts,
            remittedSelectedContracts: updatedContracts.filter((item: ContractType) => item.selected),
          });
        },

        getPendingContracts: rxMethod<void>(pipe(mergeMap(_getPendingContracts))),
        checkPendingContractsWarnings: rxMethod<void>(
          // exhaustMap: ignore overlapping triggers (direct call + NavigationEnd on emulate)
          // while a getpendingcontracts request is in flight, so the warning popup fires once.
          pipe(
            exhaustMap(() => {
              if (store.pendingContractsLoaded()) {
                return of(null);
              }

              return _getPendingContracts();
            }),
            tap(() => {
              const contracts = store.pendingGridContracts();
              const showWarning30 = contracts.some((contract: ContractType) => contract.tagCode === 'A');
              const showWarning45 = contracts.some((contract: ContractType) => contract.tagCode === 'B');
              const showWarning60 = contracts.some((contract: ContractType) => contract.tagCode === 'C');
              const showWarning90 = contracts.some((contract: ContractType) => contract.tagCode === 'D');

              const notification = {
                type: PhNotificationType.inaudit,
                value: { message: _getMessage('B'), title: translate.instant('pendingDaysPopup.title') },
              };
              if (showWarning30) {
                notificationService.push({
                  ...notification,
                  duration: 15000,
                  value: { ...notification.value, message: _getMessage('A') },
                });
              }

              if (showWarning45) {
                notificationService.push({
                  ...notification,
                  value: { ...notification.value, message: _getMessage('B') },
                });
              }

              if (showWarning60) {
                notificationService.push({
                  ...notification,
                  value: {
                    ...notification.value,
                    message: _getMessage('C'),
                  },
                });
              }

              if (showWarning90) {
                notificationService.push({
                  ...notification,
                  value: {
                    ...notification.value,
                    message: _getMessage('D'),
                  },
                });
              }
            })
          )
        ),

        getRemittedContracts: rxMethod<void>(
          pipe(
            mergeMap(() => {
              const user = userStore.user();
              const environment = configService.environment;

              return httpClient
                .post<RemittedContractResponse>(`${environment.apiUrl}/getremittedcontracts`, {
                  cmsdealernumber: user?.cmsDealerNumber || '',
                  programId: user?.programId || '',
                  allDealers: user?.allDealers || [],
                })
                .pipe(
                  tap((response: RemittedContractResponse) => {
                    if (environment.features.suppressDealerCost) {
                      remitService.suppressDealerCost(response.remittedContracts);
                    }
                  }),
                  map((contracts: RemittedContractResponse) => {
                    const remittedContracts = environment.features.remit.supportRegisteredContractSelection
                      ? contracts.remittedContracts.map((contract: ContractType) => ({ ...contract, selected: true }))
                      : contracts.remittedContracts;

                    return patchState(store, {
                      remittedContracts,
                      remittedGridContracts: remittedContracts,
                      remittedSelectedContracts: environment.features.remit.supportRegisteredContractSelection
                        ? remittedContracts
                        : [],
                      remittedFilters: RemitService.fetchRemittedContractFilters(remittedContracts),
                    });
                  }),
                  catchError((err) => throwError(() => err))
                );
            })
          )
        ),

        getCancellationAlerts: rxMethod<void>(
          pipe(
            switchMap(() => {
              const environment = configService.environment;
              const username = userStore.user()?.userName || '';
              const dealerNumber = userStore.user()?.cmsDealerNumber;
              const headers = new HttpHeaders({ clientName: environment.application, clientUserId: username });

              return httpClient
                .get<CancellationAlertsResponse>(`${environment.cancellationAlertsUrl}/alert/${dealerNumber}`, {
                  headers,
                })
                .pipe(
                  tap((alertsData) => {
                    patchState(store, { cancellationAlerts: alertsData });

                    alertsData?.data.cancellationAlertDetails?.forEach((alert) => {
                      let type: CancellationUrlType = 'home';
                      let message: string = alert.requestCategory;
                      switch (alert.requestCategory) {
                        case 'Customer/Lienholder Initiated':
                          type = 'requestInitiated';
                          message = `You have ${alert.requestCount} new Customer/Lienholder Initiated contract${
                            alert.requestCount > 1 ? 's' : ''
                          } cancellation to review.`;
                          break;
                        case 'Pending Customer Refund Issued':
                          type = 'refundIssued';
                          message = `You have ${alert.requestCount} new cancellation${
                            alert.requestCount > 1 ? 's' : ''
                          } Pending Customer/Lienholder Refund Issued confirmation.`;
                          break;
                        default:
                          console.error('Cancellation Alerts Request Category value is not expected');
                          break;
                      }
                      notificationService.push({
                        type: PhNotificationType.denied,
                        value: {
                          message: `${message}`,
                          title: 'Cancellation Alert',
                        },
                        actions: [
                          {
                            text: 'Review',
                            act: (ref) => {
                              ref.close();
                              const url = cancellationService.getCancellationUrl(type);
                              window.open(url);
                            },
                          },
                        ],
                      });
                    });
                  }),
                  catchError((error) => throwError(() => error))
                );
            })
          )
        ),
        submitAllPendingRemit: rxMethod<void>(
          pipe(
            switchMap(() => {
              const environment = configService.environment;

              return contractSaveFacade
                .submitPendingRemit(
                  store.pendingToRemitContracts(),
                  store.pendingToDeclineContracts(),
                  userStore.user() as UserResponse
                )
                .pipe(
                  map((response: PendingContractResponse) => _enrichContracts(response)),
                  map((response: PendingContractResponse) => {
                    const pendingFilters = RemitService.fetchPendingContractFilters(response.pendingContracts);
                    response = RemitService.resetContracts(response);

                    return {
                      submittedRemittedRequest: store.pendingToRemitContracts(),
                      contracts: response,
                      filters: pendingFilters,
                    };
                  }),
                  catchError((error) => throwError(() => error)),
                  tap(({ submittedRemittedRequest, contracts, filters }) => {
                    const enrichedPendingContracts = environment.features.remit.disableNewerContractsIf60DaysOldExist
                      ? _disableNewerContractsIf60DaysOldExist(contracts.pendingContracts)
                      : contracts.pendingContracts;

                    patchState(store, {
                      pendingContracts: enrichedPendingContracts,
                      pendingGridContracts: enrichedPendingContracts,
                      pendingToRemitContracts: contracts.pendingToRemitContracts,
                      pendingToDeclineContracts: contracts.pendingToDeclineContracts,
                      pendingContractFilters: filters,
                      // copies over submitted request for printing
                      submittedPendingContracts: submittedRemittedRequest,
                    });
                    dialog.open(RemitSubmittedPopupComponent, { data: { contracts } }).afterClosed();
                  })
                );
            })
          )
        ),

        clearPendingContracts(): void {
          patchState(store, {
            pendingContractsLoaded: false,
            pendingContracts: [],
            pendingGridContracts: [],
            pendingToRemitContracts: [],
            pendingToDeclineContracts: [],
            pendingContractFilters: new SearchFilter(),
          });
        },
      };
    }
  )
);
